import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    MdPlayArrow,
    MdPause,
    MdSkipNext,
    MdSkipPrevious,
    MdRemove,
    MdVolumeUp,
    MdCropSquare
} from 'react-icons/md';
import { useTTSContext } from 'contexts/TTSContext';

import './index.css';

const PANEL_WIDTH = 280;
const PANEL_HEIGHT_EXPANDED = 168;
const PANEL_HEIGHT_COLLAPSED = 40;
const TITLE_BAR_HEIGHT = 40;
const MIN_VISIBLE_PX = 40;
const STORAGE_KEY = 'ttsPanelPosition';

function defaultPosition(): { top: number; left: number } {
    if (typeof window === 'undefined') return { top: 80, left: 16 };
    return {
        top: 80,
        left: Math.max(16, window.innerWidth - PANEL_WIDTH - 16)
    };
}

function loadPosition(): { top: number; left: number } {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) {
            const parsed = JSON.parse(raw);
            if (typeof parsed.top === 'number' && typeof parsed.left === 'number') {
                return parsed;
            }
        }
    } catch {
        // ignore
    }
    return defaultPosition();
}

function clampPosition(pos: { top: number; left: number }, collapsed: boolean) {
    if (typeof window === 'undefined') return pos;
    const height = collapsed ? PANEL_HEIGHT_COLLAPSED : PANEL_HEIGHT_EXPANDED;
    const maxLeft = window.innerWidth - MIN_VISIBLE_PX;
    const minLeft = MIN_VISIBLE_PX - PANEL_WIDTH;
    const maxTop = window.innerHeight - TITLE_BAR_HEIGHT;
    const minTop = 0;
    return {
        top: Math.min(maxTop, Math.max(minTop, pos.top)),
        left: Math.min(maxLeft, Math.max(minLeft, pos.left))
    };
}

export default function ReadAloudPanel() {
    const {
        rate,
        isSpeaking,
        isPaused,
        playPause,
        next,
        prev,
        setRate
    } = useTTSContext();

    const [collapsed, setCollapsed] = useState(false);
    const [position, setPosition] = useState(loadPosition);

    const draggingRef = useRef(false);
    const dragOffsetRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(position));
    }, [position]);

    useEffect(() => {
        const onResize = () => setPosition(p => clampPosition(p, collapsed));
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [collapsed]);

    const onPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
        if ((e.target as HTMLElement).closest('.readAloudPanel__iconButton')) return;
        draggingRef.current = true;
        dragOffsetRef.current = {
            x: e.clientX - position.left,
            y: e.clientY - position.top
        };
        (e.currentTarget as Element).setPointerCapture(e.pointerId);
    }, [position]);

    const onPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
        if (!draggingRef.current) return;
        const next = clampPosition({
            left: e.clientX - dragOffsetRef.current.x,
            top: e.clientY - dragOffsetRef.current.y
        }, collapsed);
        setPosition(next);
    }, [collapsed]);

    const onPointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
        if (!draggingRef.current) return;
        draggingRef.current = false;
        try {
            (e.currentTarget as Element).releasePointerCapture(e.pointerId);
        } catch {
            // ignore
        }
    }, []);

    const playing = isSpeaking && !isPaused;

    return (
        <div
            className={`readAloudPanel ${collapsed ? 'readAloudPanel--collapsed' : ''}`}
            style={{ top: position.top, left: position.left }}
            role='region'
            aria-label='Read Aloud controls'
        >
            <div
                className='readAloudPanel__titleBar'
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
            >
                <MdVolumeUp className={`readAloudPanel__titleIcon ${playing ? 'readAloudPanel__titleIcon--active' : ''}`} />
                <span className='readAloudPanel__title'>Read Aloud</span>
                {collapsed && (
                    <button
                        className='readAloudPanel__iconButton readAloudPanel__iconButton--compact'
                        onClick={playPause}
                        aria-label={playing ? 'Pause' : 'Play'}
                    >
                        {playing ? <MdPause /> : <MdPlayArrow />}
                    </button>
                )}
                <button
                    className='readAloudPanel__iconButton readAloudPanel__iconButton--compact'
                    onClick={() => setCollapsed(c => !c)}
                    aria-label={collapsed ? 'Expand' : 'Minimize'}
                >
                    {collapsed ? <MdCropSquare /> : <MdRemove />}
                </button>
            </div>

            {!collapsed && (
                <div className='readAloudPanel__body'>
                    <div className='readAloudPanel__controlRow'>
                        <button
                            className='readAloudPanel__iconButton readAloudPanel__iconButton--lg'
                            onClick={prev}
                            aria-label='Previous'
                        >
                            <MdSkipPrevious />
                        </button>
                        <button
                            className='readAloudPanel__iconButton readAloudPanel__iconButton--lg readAloudPanel__iconButton--primary'
                            onClick={playPause}
                            aria-label={playing ? 'Pause' : 'Play'}
                        >
                            {playing ? <MdPause /> : <MdPlayArrow />}
                        </button>
                        <button
                            className='readAloudPanel__iconButton readAloudPanel__iconButton--lg'
                            onClick={next}
                            aria-label='Next'
                        >
                            <MdSkipNext />
                        </button>
                    </div>
                    <div className='readAloudPanel__labelRow'>
                        <span>Prev</span>
                        <span>{playing ? 'Pause' : isPaused ? 'Resume' : 'Play'}</span>
                        <span>Next</span>
                    </div>
                    <div className='readAloudPanel__speedRow'>
                        <span className='readAloudPanel__speedLabel'>Speed</span>
                        <button
                            className='readAloudPanel__iconButton'
                            onClick={() => setRate(rate - 0.25)}
                            disabled={rate <= 0.5}
                            aria-label='Slower'
                        >
                            –
                        </button>
                        <span className='readAloudPanel__speedValue'>{rate.toFixed(2)}×</span>
                        <button
                            className='readAloudPanel__iconButton'
                            onClick={() => setRate(rate + 0.25)}
                            disabled={rate >= 2}
                            aria-label='Faster'
                        >
                            +
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
